import React, { useCallback, useEffect, useState, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import Global from '../../../store/global-selectors';

import USPSPickupPageContent from './components/pickup-content';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import UspsPickupPlaceholder from './components/pickup-placeholder';
import UspsPickupHeader from './components/pickup-header';
import { getUspsPickupData } from '../../../store/shipments/shipments.api';
import { scheduleUspsPickup, cancelUspsPickup } from '../../../store/shipments/shipments.actions';
import { USPS_PICKUP_VIEW_ID } from '../../../components/shipping-grid/views/view-id-constants';
import { PICKUP_ACTIONS, INPUT_KEYS, generatePickupRequestBody } from './constants';
import { uspsPickupReducer } from './usps-pickup-reducer';
import { testId } from 'ui-components/services/testid-service';
import { NumberFormattingService } from '../../../services/number-formatting-service';

import style from './pickup-page.module.less';

const url = `/shipments/${USPS_PICKUP_VIEW_ID}`;

export const USPSPickupPage = () => {
  const [state, updateState] = useReducer(uspsPickupReducer, {});
  const [isLoading, setIsLoading] = useState(true);

  const shipFroms = useSelector(Global.selectSellerShipFroms) || [];
  const { shipFromId: paramId } = useParams();
  const selectedShipFrom = shipFroms.find((s) => s.shipFromId === paramId) || shipFroms[0];
  const selectedShipFromId = selectedShipFrom?.shipFromId ?? '';
  const currentUser = useSelector(Global.selectUserCurrentUserInfo);
  const [firstName, ...restOfName] = currentUser.name.split(' ');
  const lastName = restOfName.join(' ');
  const dispatch = useDispatch();

  const getPickupData = useCallback(
    (shipFromId) => {
      setIsLoading(true);
      const pickupDataPromise = getUspsPickupData(shipFromId);
      pickupDataPromise.promise
        .then((res) => {
          const { futurePickup, todaysPickup, nextAvailable } = res;
          const specialInstruction = futurePickup?.specialInstruction ?? '';
          const initialCompanyValue = selectedShipFrom?.company ?? '';
          const warehouseID = parseInt(paramId, 10);

          updateState({
            type: PICKUP_ACTIONS.GET_API_DATA,
            payload: {
              todaysPickup,
              futurePickup: {
                ...futurePickup,
                firstName,
                lastName,
                specialInstruction,
                company: initialCompanyValue,
                warehouseID,
                pickupDate: nextAvailable,
              },
            },
          });
          setIsLoading(false);
        })
        .catch(() => {
          updateState({ type: PICKUP_ACTIONS.ON_API_ERROR });
          setIsLoading(false);
        });

      return pickupDataPromise;
    },
    [firstName, lastName, paramId, selectedShipFrom?.company],
  );

  useEffect(() => {
    const promise = getPickupData(selectedShipFromId);
    return () => promise.cancel();
  }, [getPickupData, selectedShipFromId]);

  const requestPickup = useCallback(() => {
    const postBody = generatePickupRequestBody(state.futurePickup);
    dispatch(scheduleUspsPickup(postBody));
  }, [dispatch, state]);

  const cancelPickup = useCallback(() => {
    dispatch(cancelUspsPickup(state.futurePickup?.pickupRequestID));
  }, [dispatch, state?.futurePickup?.pickupRequestID]);

  const handleInputChange = useCallback(
    (inputKey) => (inputValue) => {
      if (inputKey !== INPUT_KEYS.PICKUP_LOCATION) {
        updateState({
          type: PICKUP_ACTIONS.INPUT_CHANGE,
          payload: { key: inputKey, updatedValue: inputValue },
        });
        return;
      }

      updateState({
        type: PICKUP_ACTIONS.DROPDOWN_CHANGE,
        payload: { key: inputKey, location: inputValue },
      });
    },
    [],
  );

  const postPickupShipFromChangedMessageToV2Iframe = useCallback(() => {
    const pickupFrame = document.getElementById('pickupIframe');
    const message = {
      eventType: 'v3ChangePickupShipFrom',
      shipFromId: NumberFormattingService.parseStringToNumber(selectedShipFromId),
    };
    if (pickupFrame) {
      pickupFrame.contentWindow.postMessage(JSON.stringify(message), '*');
    }
  }, [selectedShipFromId]);

  useEffect(() => {
    postPickupShipFromChangedMessageToV2Iframe(selectedShipFromId);
  }, [postPickupShipFromChangedMessageToV2Iframe, selectedShipFromId]);

  const { hasApiError } = state;

  const renderPageContent = () => {
    if (hasApiError || !selectedShipFromId) {
      return <UspsPickupPlaceholder hasApiError={hasApiError} />;
    }
    return (
      <USPSPickupPageContent
        cancelPickup={cancelPickup}
        handleInputChange={handleInputChange}
        pickupData={state}
        requestPickup={requestPickup}
        shipFrom={selectedShipFrom}
      />
    );
  };

  if (!paramId && shipFroms.length) {
    return <Redirect to={`${url}/${shipFroms[0].shipFromId}`} />;
  }
  return (
    <div className={style['pickup-page']} {...testId('pickup-header')}>
      <UspsPickupHeader shipFroms={shipFroms} />
      {isLoading && <LoadingIndicator />}
      {!isLoading && renderPageContent()}
    </div>
  );
};
